import { storeToRefs } from 'pinia'
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useAuthStore } from '~/stores/auth.js'
import { useModals } from '~/composables/useModals.js'
import { useUpload } from '~/composables/useUpload.js'
import { useImpersonation } from '~/composables/useImpersonation.js'
import ModalLogin from '~/components/modals/ModalLogin.vue'
import ModalPasswordReset from '~/components/modals/ModalPasswordReset.vue'

export function useNavbar() {
  const upload = useUpload()
  const authStore = useAuthStore()
  const { authenticated } = storeToRefs(authStore)
  const { topClass: impersonationTopClass } = useImpersonation()

  const route = useRoute()
  const router = useRouter()

  const { component: modalComponent, modalProps, open: openModal } = useModals()

  const openLogin = (redirect) => openModal(ModalLogin, {
    redirect: redirect || '/dashboard',
  })

  const mobileOpen = ref(false)
  const toggleMobile = () => { mobileOpen.value = !mobileOpen.value }
  const closeMobile = () => { mobileOpen.value = false }

  const onLoginClick = () => {
    mobileOpen.value = false
    openLogin()
  }

  watch(() => route.fullPath, closeMobile)

  const navRef = ref(null)

  const onKeydown = (e) => { if (e.key === 'Escape') closeMobile() }
  const onClickOutside = (e) => {
    if (mobileOpen.value && navRef.value && !navRef.value.contains(e.target)) {
      closeMobile()
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', onKeydown)
    document.addEventListener('click', onClickOutside)
  })
  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown)
    document.removeEventListener('click', onClickOutside)
  })

  const stripQuery = (...keys) => {
    const next = { ...route.query }
    for (const k of keys) delete next[k]
    router.replace({ path: route.path, query: next })
  }

  onMounted(() => {
    if (route.query.login === '1' && !authStore.authenticated) {
      const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/dashboard'
      openLogin(redirect)
      stripQuery('login', 'redirect')
      return
    }
    if (route.query['password-reset'] === '1') {
      const token = typeof route.query.token === 'string' ? route.query.token : ''
      openModal(ModalPasswordReset, { token })
      stripQuery('password-reset', 'token')
    }
  })

  return {
    authenticated,
    impersonationTopClass,
    navRef,
    mobileOpen,
    toggleMobile,
    closeMobile,
    onLoginClick,
    startUpload: () => upload.triggerUpload(),
    modalComponent,
    modalProps,
  }
}
